// src/pages/settings.js
import React, { useEffect, useState } from 'react';
import supabase from '../config/supabaseClient';
import '../components/settings.css';
import imageCompression from 'browser-image-compression';
import ProfileCapsule from './profilecapsule';
import DeleteAccountIcon from '../icons/deleteaccounticon';
import LockedProfileIcon from '../icons/lockedprofileicon';
import ChangePasswordIcon from '../icons/changepasswordicon';
import UnlockedProfileIcon from '../icons/unlockedprofileicon';
import SaveIcon from '../icons/saveicon';
import UserEffectsIcon from '../icons/usereffectsicon';
import DashboardIcon from '../icons/dashboardicon';
import YesIcon from '../icons/yesicon';
import NoIcon from '../icons/noicon';


export default function Settings() {
  const [userEmail, setUserEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [avatarUrl, setAvatarUrl] = useState('');
  const [avatarNickname, setAvatarNickname] = useState('');
  const [userId, setUserId] = useState('');
  const [bio, setBio] = useState('');
  const [location, setLocation] = useState('');
  const [phone, setPhone] = useState('');
  const [userPhoto, setUserPhoto] = useState('');
  const [backdropUrl, setBackdropUrl] = useState('');
  const [avatarOrPic, setAvatarOrPic] = useState('avatar');
  const [socialLinks, setSocialLinks] = useState({
    facebook: '',
    instagram: '',
    twitter: '',
    snapchat: '',
    whatsapp: '',
    tiktok: ''
  });

  const [hasChanges, setHasChanges] = useState(false);
  const [showAvatarPicker, setShowAvatarPicker] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState({ type: null, open: false, step: 'choice' });
  const [allAvatars, setAllAvatars] = useState([]);
  const [filteredAvatars, setFilteredAvatars] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const avatarsPerPage = 25;
  const [isPrivate, setIsPrivate] = useState(false);
  const [loading, setLoading] = useState(true);
  const [settingsLocked, setSettingsLocked] = useState(true);
  const [showUnlockPrompt, setShowUnlockPrompt] = useState(false);
  const [showSaveConfirmation, setShowSaveConfirmation] = useState(false);
  const [profileTheme, setProfileTheme] = useState('default');
  const [avatarEffect, setAvatarEffect] = useState('default');
  const [showThemeModal, setShowThemeModal] = useState(false);


  const handleRequestUnlock = () => {
    setShowUnlockPrompt(true);
  };

  const handleUnlockConfirm = (confirmed) => {
    if (confirmed) {
      setSettingsLocked(false);
    }
    setShowUnlockPrompt(false);
  };



  const avatarStyles = [
    'bottts', 'adventurer', 'avataaars', 'big-ears', 'big-smile',
    'croodles', 'fun-emoji', 'identicon', 'lorelei', 'micah',
    'miniavs', 'notionists', 'open-peeps', 'pixel-art', 'pixel-art-neutral',
    'rings', 'shapes', 'thumbs'
  ];

  const generateAvatarList = (pageNum = 1, perPage = avatarsPerPage) => {
    const list = [];
    const start = (pageNum - 1) * perPage;
    const end = start + perPage;
    for (let i = start; i < end; i++) {
      const style = avatarStyles[i % avatarStyles.length];
      const seed = `page${pageNum}-avatar${i}`;
      list.push({
        style,
        seed,
        url: `https://api.dicebear.com/8.x/${style}/png?seed=${seed}&size=350`
      });
    }
    return list;
  };

  const fetchProfile = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      setUserEmail(user.email);
      setUserId(user.id);

      const { data: profile, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('user_id', user.id)
        .single();

      if (error) {
        console.log("Error fetching profile:", error);
        return;
      }

      if (profile) {
        setFirstName(profile.first_name || '');
        setLastName(profile.last_name || '');
        setAvatarUrl(profile.user_avatar || '');
        setAvatarNickname(profile.avatar_nickname || '');
        setUserPhoto(profile.user_photo || '');
        setBio(profile.bio || '');
        setLocation(profile.location || '');
        setPhone(profile.phone || '');
        setBackdropUrl(profile.backdrop || '');
        setAvatarOrPic(profile.avatar_or_pic || 'avatar');
        setIsPrivate(profile.is_private ?? false);
        setSocialLinks({
          facebook: profile.social_facebook || '',
          instagram: profile.social_instagram || '',
          twitter: profile.social_x || '',
          snapchat: profile.social_snapchat || '',
          whatsapp: profile.social_whatsapp || '',
          tiktok: profile.social_tiktok || '',
        });
        setProfileTheme(profile.profile_theme || 'default');
        setAvatarEffect(profile.avatar_effect || 'default');
      }
    } finally {
      setLoading(false); // ✅ This ensures loading state flips off once done
    }
  }; 


    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
      fetchProfile();
      const avatars = generateAvatarList(1);
      setAllAvatars(avatars);
      setFilteredAvatars(avatars);
    }, []);


  const handleSave = async () => {
    const updates = {
      //user_id: userId,
      user_avatar: avatarUrl,
      avatar_nickname: avatarNickname,
      user_photo: userPhoto,
      avatar_or_pic: avatarOrPic,
      bio,
      location,
      phone,
      backdrop: backdropUrl,
      social_facebook: socialLinks.facebook,
      social_instagram: socialLinks.instagram,
      social_x: socialLinks.twitter,
      social_snapchat: socialLinks.snapchat,
      social_whatsapp: socialLinks.whatsapp,
      social_tiktok: socialLinks.tiktok,
      is_private: isPrivate,
      profile_theme: profileTheme,
      avatar_effect: avatarEffect,

    };

    console.log("Saving profile with updates:", updates);

    const { error } = await supabase
      .from('profiles')
      .update(updates)
      .eq('user_id', userId);

    if (!error) {
      setHasChanges(false);
      await fetchProfile();
      setShowSaveConfirmation(true);
      setTimeout(() => setShowSaveConfirmation(false), 3000);
    } else {
      console.error("Supabase error:", error);
      alert('Error saving changes.');
    }


  };

  const handleSocialClick = (platform) => {
    const url = prompt(`Enter your ${platform} URL:`, socialLinks[platform] || '');
    if (url !== null) {
      setHasChanges(true);
      setSocialLinks(prev => ({ ...prev, [platform]: url }));
    }
  };

  const openUploadModal = (type) => {
    setShowUploadModal({ type, open: true, step: 'choice' });
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
    const avatars = generateAvatarList(newPage);
    setAllAvatars(avatars);
    setFilteredAvatars(avatars);
  };

  const handleFileUpload = async (file) => {
    const options = {
      maxSizeMB: 1,
      maxWidthOrHeight: 600,
      useWebWorker: true,
    };

    try {
      const compressedFile = await imageCompression(file, options);
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64 = reader.result;
        if (showUploadModal.type === 'photo') setUserPhoto(base64);
        if (showUploadModal.type === 'backdrop') setBackdropUrl(base64);
        setHasChanges(true);
      };
      reader.readAsDataURL(compressedFile);
    } catch (error) {
      console.error('Image compression error:', error);
      alert('Failed to process image.');
    }
  };

  if (loading) return <div className="settings-loading">Loading profile...</div>;


  return (
    <div className="settings-container">

        <ProfileCapsule profile={{
          avatar_or_pic: avatarOrPic,
          user_photo: userPhoto,
          user_avatar: avatarUrl,
          first_name: firstName,
          last_name: lastName,
          avatar_nickname: avatarNickname,
          bio,
          location,
          phone,
          user_email: userEmail,
          backdrop: backdropUrl,
          social_facebook: socialLinks.facebook,
          social_instagram: socialLinks.instagram,
          social_x: socialLinks.twitter,
          social_snapchat: socialLinks.snapchat,
          social_whatsapp: socialLinks.whatsapp,
          social_tiktok: socialLinks.tiktok
        }} />

  
      <div className="account-panel">
        <fieldset disabled={settingsLocked} style={{ border: 'none', padding: 0, margin: 0 }}>
        <div className="account-header-row">
        <h2 className="account-title">Welcome, {firstName}</h2>
          <div className="profile-image-toggle">
            <span
              className={avatarOrPic === 'photo' ? 'toggle-active' : 'toggle-inactive'}
              onClick={() => {
                if (!settingsLocked) {
                  setAvatarOrPic('photo');
                  setHasChanges(true);
                }
              }}
            >
              Profile Pic
            </span>
            {' | '}
            <span
              className={avatarOrPic === 'avatar' ? 'toggle-active' : 'toggle-inactive'}
              onClick={() => {
                if (!settingsLocked) {
                  setAvatarOrPic('avatar');
                  setHasChanges(true);
                }
              }}
            >
              Avatar
            </span>
          </div>

        </div>

        <div className="media-row">
          <div className="media-box" onClick={() => {
              if (!settingsLocked) openUploadModal('photo');}}>

            <img src={userPhoto || '/default-profile.png'} alt="Profile" className="media-image" />
            <div className="media-label">Profile Pic</div>
          </div>
          <div className="media-box" onClick={() => {
              if (!settingsLocked) setShowAvatarPicker(true);}}>

            <img src={avatarUrl || '/default-avatar.png'} alt="Avatar" className="media-image" />
            <div className="media-label">Avatar</div>
          </div>
          <div className="media-box" onClick={() => {
              if (!settingsLocked) openUploadModal('backdrop');}}>

            <img src={backdropUrl || '/default-backdrop.png'} alt="Backdrop" className="media-image" />
            <div className="media-label">Profile Backdrop</div>
          </div>
        </div>

        <div className="account-row">
          <div className="account-label">Nickname</div>
          <input type="text" className="account-input" value={avatarNickname} onChange={e => { setAvatarNickname(e.target.value); setHasChanges(true); }} />
        </div>
        <div className="account-row">
          <div className="account-label">Email</div>
          <input type="email" className="account-input" value={userEmail} onChange={e => { setUserEmail(e.target.value); setHasChanges(true); }} />
        </div>
        <div className="account-row">
          <div className="account-label">Phone</div>
          <input type="text" className="account-input" value={phone} onChange={e => { setPhone(e.target.value); setHasChanges(true); }} />
        </div>
        <div className="account-row">
          <div className="account-label">Location</div>
          <input type="text" className="account-input" value={location} onChange={e => { setLocation(e.target.value); setHasChanges(true); }} />
        </div>

          <div className="account-row bio-with-button">
            <div style={{ flex: 1 }}>
              <div className="account-label">Bio</div>
              <textarea
                className="account-textarea"
                value={bio}
                onChange={(e) => {
                  setBio(e.target.value);
                  setHasChanges(true);
                }}
              />
            </div>
          </div>

        <div className="account-row">
          <div className="account-label">Profile Privacy</div>
          <label className="switch">
            <input
              type="checkbox"
              checked={!isPrivate}
              onChange={() => {
                setIsPrivate(!isPrivate);
                setHasChanges(true);
              }}
            />
            <span className="slider round"></span>
            <span style={{ marginLeft: '10px' }}>{isPrivate ? 'Private' : 'Public'}</span>
          </label>
        </div>



        <div className="account-row">
          <div className="account-label">Social Media</div>
          <div className="account-social-icons">
            <i className="fab fa-facebook" onClick={() => {
                if (!settingsLocked) handleSocialClick('facebook');}}/>
            <i className="fab fa-instagram" onClick={() => {
                if (!settingsLocked) handleSocialClick('instagram');}}/>
            <i className="fab fa-x-twitter" onClick={() => {
                if (!settingsLocked) handleSocialClick('twitter');}}/>
            <i className="fab fa-snapchat-ghost" onClick={() => {
                if (!settingsLocked) handleSocialClick('snapchat');}}/>
            <i className="fab fa-whatsapp" onClick={() => {
                if (!settingsLocked) handleSocialClick('whatsapp');}}/>
            <i className="fab fa-tiktok" onClick={() => {
                if (!settingsLocked) handleSocialClick('tiktok');}}/>
          </div>
        </div>

    </fieldset>
          <div className="account-settings-actions">
            {/* Left: Lock/Unlock */}
            {settingsLocked ? (
              <button
                className="settings-icon-button"
                onClick={handleRequestUnlock}
                title="Unlock Settings"
              >
                <LockedProfileIcon size={26} />
              </button>
            ) : (
              <button
                className="settings-icon-button"
                onClick={() => setSettingsLocked(true)}
                title="Lock Settings"
              >
                <UnlockedProfileIcon size={26} />
              </button>
            )}

            {/* Right: Password + Delete (only when unlocked) */}
            {!settingsLocked && (
              <div className="settings-actions-right">
                {hasChanges && (
                  <button
                    className="settings-icon-button"
                    title="Save Changes"
                    onClick={handleSave}
                  >
                    <SaveIcon size={24} />
                  </button>
                )}

                {showSaveConfirmation && (
                  <span className="save-message-inline">Changes Saved</span>
                )}

                <button className="settings-icon-button" title="Customize Theme" onClick={() => setShowThemeModal(true)}>
                  <UserEffectsIcon size={24} />
                </button>

                <button className="settings-icon-button" title="Change Password">
                  <ChangePasswordIcon size={24} />
                </button>

                <button className="settings-icon-button delete-account-button" title="Delete Account">
                  <DeleteAccountIcon size={24} />
                </button>
              </div>
            )}
          </div>
    </div>

      {/* Avatar Picker Modal */}
      {showAvatarPicker && (
        <div className="avatar-modal-overlay">
          <div className="avatar-modal-content">
            <h3>Select Your Avatar</h3>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => {
                const val = e.target.value.toLowerCase();
                setSearchTerm(val);
                const filtered = allAvatars.filter(a => a.style.includes(val));
                setFilteredAvatars(filtered);
                setPage(1);
              }}
              className="avatar-search"
              placeholder="Search styles (e.g. emoji, pixel)"
            />
            <div className="avatar-grid">
              {filteredAvatars.slice(0, avatarsPerPage).map((avatar, i) => (
                <img
                  key={i}
                  src={avatar.url}
                  alt={avatar.style}
                  className={avatar.url === avatarUrl ? 'selected' : ''}
                  onClick={() => {
                    setAvatarUrl(avatar.url);
                    setHasChanges(true);
                    setShowAvatarPicker(false);
                  }}
                />
              ))}
            </div>
            <div className="avatar-pagination">
              <button onClick={() => handlePageChange(Math.max(1, page - 1))} disabled={page === 1}>Prev</button>
              <span>Page {page}</span>
              <button onClick={() => handlePageChange(page + 1)}>Next</button>
            </div>
            <div style={{ textAlign: 'center', marginTop: '15px' }}>
              <button onClick={() => setShowAvatarPicker(false)}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* Upload Modal (Profile/Backdrop) */}
      {showUploadModal.open && (
        <div className="upload-modal-overlay">
          <div className="upload-modal-content new-style">
            <label className="upload-label">
              Upload Image
              <input
                type="file"
                accept="image/*"
                onChange={(e) => {
                  const file = e.target.files[0];
                  if (!file) return;

                  if (file.size > 5 * 1024 * 1024) {
                    const proceed = window.confirm('This file is over 5MB and may take longer to process. Continue?');

                    if (!proceed) return;
                  }

                  handleFileUpload(file, showUploadModal.type);
                }}
              />


            </label>
            <div className="upload-divider"><hr /><span>OR</span><hr /></div>
            <input
              type="text"
              className="upload-url-input"
              placeholder="Enter image URL"
              onChange={(e) => {
                if (showUploadModal.type === 'photo') setUserPhoto(e.target.value);
                if (showUploadModal.type === 'backdrop') setBackdropUrl(e.target.value);
                setHasChanges(true);
              }}
            />
            <div className="upload-modal-buttons bottom-right">
              <button onClick={() => setShowUploadModal({ open: false, type: null, step: 'choice' })}>Cancel</button>
              <button onClick={() => setShowUploadModal({ open: false, type: null, step: 'choice' })}>Done</button>
            </div>
          </div>
        </div>
      )}

      {showUnlockPrompt && (
        <div className="modal-overlay">
          <div className="modal-content">
            <p>Would you like to make changes to your account?</p>
            <div className="modal-buttons">
              <button onClick={() => handleUnlockConfirm(true)}>
                <YesIcon style={{ marginRight: '0.5rem' }} />
              </button>
              <button onClick={() => handleUnlockConfirm(false)}>
                <NoIcon style={{ marginRight: '0.5rem' }} />
              </button>
            </div>
          </div>
        </div>
      )}


    </div>
  );
}
